import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
} from '@mui/material';
import { CompareArrows as CompareIcon, Receipt as ReceiptIcon, Search as SearchIcon, ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import MainLayout from '../../components/layout/MainLayout';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { format } from 'date-fns';

interface ReceiptEditLog {
  id: string;
  receiptId: string;
  editedBy: string;
  oldData: string;
  newData: string;
  reason: string;
  createdAt: string;
  editor: {
    firstName: string;
    lastName: string;
    email: string;
  };
}

const ReceiptEditLogList: React.FC = () => {
  const navigate = useNavigate();
  const [logs, setLogs] = useState<ReceiptEditLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [receiptIdFilter, setReceiptIdFilter] = useState('');
  const [selectedLog, setSelectedLog] = useState<ReceiptEditLog | null>(null);

  useEffect(() => {
    fetchLogs();
  }, []);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (receiptIdFilter) params.append('receiptId', receiptIdFilter);

      const response = await axios.get(`/system-settings/logs/receipt-edits?${params.toString()}`);
      setLogs(response.data);
    } catch (error) {
      console.error('Error fetching logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleViewDiff = (log: ReceiptEditLog) => {
    setSelectedLog(log);
  };

  const handleCloseDiff = () => {
    setSelectedLog(null);
  };

  const formatJSON = (jsonString: string) => {
    try {
      return JSON.stringify(JSON.parse(jsonString), null, 2);
    } catch (e) {
      return jsonString;
    }
  };

  return (
    <MainLayout>
      <Container maxWidth="lg">
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 1 }}>
          <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/system-settings/user-roles')} sx={{ mr: 2 }}>
            Back
          </Button>
          <ReceiptIcon color="primary" sx={{ fontSize: 32 }} />
          <Typography variant="h4">Receipt Edit Logs</Typography>
        </Box>

        <Paper sx={{ p: 2, mb: 3 }}>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="Receipt ID (optional)"
              value={receiptIdFilter}
              onChange={(e) => setReceiptIdFilter(e.target.value)}
              sx={{ minWidth: 300 }}
            />
            <Button
              variant="contained"
              startIcon={<SearchIcon />}
              onClick={fetchLogs}
            >
              Search
            </Button>
          </Box>
        </Paper>

        <Paper sx={{ width: '100%', mb: 2 }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Date & Time</TableCell>
                  <TableCell>Receipt ID</TableCell>
                  <TableCell>Edited By</TableCell>
                  <TableCell>Reason</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {logs.map((log) => (
                  <TableRow key={log.id}>
                    <TableCell>
                      {format(new Date(log.createdAt), 'dd MMM yyyy HH:mm:ss')}
                    </TableCell>
                    <TableCell>{log.receiptId}</TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">
                        {log.editor.firstName} {log.editor.lastName}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {log.editor.email}
                      </Typography>
                    </TableCell>
                    <TableCell>{log.reason}</TableCell>
                    <TableCell align="center">
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<CompareIcon />}
                        onClick={() => handleViewDiff(log)}
                      >
                        View Diff
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {!loading && logs.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      No edit logs found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        {/* Diff Viewer Dialog */}
        <Dialog open={!!selectedLog} onClose={handleCloseDiff} maxWidth="lg" fullWidth>
          <DialogTitle>Receipt Edit Details</DialogTitle>
          <DialogContent dividers>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="h6" gutterBottom color="error">
                  Old Data
                </Typography>
                <Paper variant="outlined" sx={{ p: 2, bgcolor: '#fff5f5', maxHeight: '60vh', overflow: 'auto' }}>
                  <pre style={{ margin: 0, fontSize: '0.8rem' }}>
                    {selectedLog && formatJSON(selectedLog.oldData)}
                  </pre>
                </Paper>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="h6" gutterBottom color="success.main">
                  New Data
                </Typography>
                <Paper variant="outlined" sx={{ p: 2, bgcolor: '#f6ffed', maxHeight: '60vh', overflow: 'auto' }}>
                  <pre style={{ margin: 0, fontSize: '0.8rem' }}>
                    {selectedLog && formatJSON(selectedLog.newData)}
                  </pre>
                </Paper>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDiff}>Close</Button>
          </DialogActions>
        </Dialog>
      </Container>
    </MainLayout>
  );
};

export default ReceiptEditLogList;
